import React from "react"
import { graphql } from "gatsby"
import { Helmet } from "react-helmet"

import Layout from "../components/Layout"
import vmLogo from "../images/logo/logo_black_LST_wo_tag.png"
import Clients from "../components/Clients"
import Reviews from "../components/Reviews"
import Registration from "../components/Registration"
import News from "../components/News"
import HomePageIntro from "../components/HomePageIntro"
import ServiceMosaic from "../components/home/ServiceMosaic"
import OriginStory from "../components/OriginStory"
import StartWhereYouAre from "../components/home/StartWhereYouAre"
import SolutionsGrid from "../components/home/SolutionsGrid"
import ProofBand from "../components/home/ProofBand"
import Objections from "../components/home/Objections"
import OpenDoor from "../components/home/OpenDoor"
import ScrollReveal from "../components/home/ScrollReveal"

import "../styles/home.scss"
import "../styles/animate.css"
import "../styles/serviceMosaic.scss"
import "../styles/bannerCards.scss"
import "../styles/clients.scss"
import "../styles/reviews.scss"
import "../styles/originStory.scss"
import "../styles/news.scss"
import "../styles/registration.scss"
import "../styles/homeSections.scss"

const SITE = "https://www.visiomate.com"

// Organization schema (spec 6.2). Social profiles mirror the ones linked in
// the footer; phone numbers and emails are the ones published on /contact.
// The "+1 234 567 8900" on the contact form is a field placeholder, not a
// real line, so it is deliberately not published here.
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Visiomate",
  url: SITE,
  logo: `${SITE}${vmLogo}`,
  description:
    "AI-native software development company delivering web, mobile and Odoo ERP solutions for small and mid-sized businesses.",
  foundingDate: "2018",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Kasur Rd",
    addressLocality: "Lahore",
    addressRegion: "Punjab",
    postalCode: "54700",
    addressCountry: "PK",
  },
  sameAs: [
    "https://www.linkedin.com/company/visiomate",
    "https://www.facebook.com/visiomateofficial",
    "https://www.instagram.com/visiomate_",
    "https://www.youtube.com/@visiomate",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: "+92-315-7798505",
      email: "business@visiomate.com",
      areaServed: ["US", "GB", "AE", "PK"],
      availableLanguage: ["English"],
    },
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: "+92-304-4797665",
      email: "info@visiomate.com",
      areaServed: ["US", "GB", "AE", "PK"],
      availableLanguage: ["English"],
    },
  ],
}

const Home = ({ data }) => {
  return (
    <Layout
      className="home-page-intro"
      data={data}
      style={{
        background: `white`,
      }}
      pageTitle={"Visiomate - AI-Native Software Development Company"}
      descrip = {"Streamline your business with our expertise in web & mobile app development and Odoo ERP implementation. We deliver customized solutions for growth of our customers."}
    >
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      {/* Order per LANDING-PLAN §2. The page is one conversation: the hero
          poses the problem, the hinge lets the reader identify themselves,
          then answer → evidence → objections. The service mosaic sits after the
          offers so it reads as "broader than a package" and feeds the open
          door, rather than interrupting before the reader has been addressed.
          OriginStory moves late — credibility is reassurance after interest,
          not the hook (JOURNEY-ANALYSIS §1.1). */}
      <ScrollReveal />
      <HomePageIntro data={data} />
      <StartWhereYouAre />
      <SolutionsGrid />
      <ProofBand />
      <Objections />
      <ServiceMosaic />
      <OpenDoor />
      <OriginStory />
      <Clients data={data} />
      <Reviews data={data} />
      <Registration data={data} />
      <News isAbout={false} />
    </Layout>
  )
}

export default Home

export const query = graphql`
  query Images {
    SNGPL: file(relativePath: { eq: "mobile/SNGPLApp-01.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    SBP: file(relativePath: { eq: "mobile/statebankapp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    EMS: file(relativePath: { eq: "EMS/ems_top_header.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    FarmManagementSystem: file(
      relativePath: { eq: "FarmManagementSystem/pf.jpg" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    SoharPort: file(relativePath: { eq: "soharport 1.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    KM: file(relativePath: { eq: "image 5.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    DistributionD365: file(
      relativePath: { eq: "DistributionD365/dist_top_header.png" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    PowerBI: file(relativePath: { eq: "power-BI.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Azure: file(relativePath: { eq: "azure.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    TenderAssetRecordManagement: file(
      relativePath: { eq: "TenderAssetRecordManagement/tender.jpg" }
    ) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    ViztosPos: file(relativePath: { eq: "ViztosPos/viztos_top_header.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Odoo2: file(relativePath: { eq: "Odoo/odoo.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Custom: file(relativePath: { eq: "Custom/CustDevelopment.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    D365_2: file(relativePath: { eq: "D365/D365_2.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Bigjerry: file(relativePath: { eq: "mobile/BigJerryApp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    UmrahShuttle: file(relativePath: { eq: "mobile/umrahshuttleapp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Visioedge: file(relativePath: { eq: "caseStudies/visioedgeheader.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    VMLogoBlack: file(relativePath: { eq: "logo_black_LST.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    VMLogoWhite: file(relativePath: { eq: "logo_white_LST.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    arrowImageWhite: file(relativePath: { eq: "Mask group (6).png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    thumbnail: file(relativePath: { eq: "Herosection/herovideostill.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    UMTVisiomate: file(relativePath: { eq: "lifeatvisiomate/UMT_Visiomate3.jpg" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    umrahshuttleapp: file(relativePath: { eq: "mobile/umrahshuttleapp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    perimflowForMenu: file(relativePath: { eq: "news/perimflow.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    learningcircle: file(relativePath: { eq: "news/learning_circle.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    instagram: file(relativePath: { eq: "instagram.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    facebook: file(relativePath: { eq: "facebook.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    linkedin: file(relativePath: { eq: "linkedin.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    twitter: file(relativePath: { eq: "twitter_bird.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    youtube: file(relativePath: { eq: "youtube.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    arrow: file(relativePath: { eq: "arrow.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    webcaseDiv1: file(relativePath: { eq: "EMS/ems_top_header.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    webcaseDiv2: file(relativePath: { eq: "Custom/Diversepro-1.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    mobilecaseDiv1: file(relativePath: { eq: "mobile/umrahshuttleapp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    mobilecaseDiv2: file(relativePath: { eq: "mobile/BigJerryApp.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    erpcaseDiv1: file(relativePath: { eq: "DistributionD365/dist_top_header.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    erpcaseDiv2: file(relativePath: { eq: "D365/xstackintegration1.webp" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    bicaseDiv1: file(relativePath: { eq: "BI/powerbidashboard.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    bicaseDiv2: file(relativePath: { eq: "EMS/ems_top_header.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    pseb: file(relativePath: { eq: "logo-pseb 1.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    Rectangle: file(relativePath: { eq: "Rectangle.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    bgImg: file(relativePath: { eq: "Maskgroup4.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
